import type { AppContext } from 'types/common'
import type { ComponentProps } from 'svelte'
import type { ValueOf } from 'type-fest'
import component from './Group.svelte'
import { graphql, type PermissionLevel } from '$houdini'
import { v4 as uuid } from '@lukeed/uuid'

export default ({ stateRouter }: AppContext) => {
	stateRouter.addState({
		name: 'app.configuration.group',
		route: 'group',
		querystringParameters: ['lastResetTime', 'lastSavedTime'],
		template: { svelte: true, component },
		async resolve(): Promise<Omit<ComponentProps<typeof component>, 'asr'>> {
			const { data } = await dataQuery.fetch()
			const groups = data?.groups ?? []
			const permissions = data?.permissions ?? []
			const userAccounts = data?.userAccounts ?? []

			return {
				groups: groups.map(
					(group): Group => ({
						...group,
						// group.userAccounts is a sparse list, so we have to map over the full userAccounts list
						userAccounts: userAccounts.map(userAccount => ({
							id: userAccount.id,
							name: userAccount.name,
							isMember: userAccount.groups?.some(userGroup => userGroup.id === group.id) ?? false,
						})),
						// group.permissions is also a sparse list
						permissions: permissions.map(permission => ({
							...permission,
							value: (group.permissions.find(p => p.id === permission.id)?.value?.toUpperCase() ?? 'NONE') as Uppercase<
								ValueOf<typeof PermissionLevel>
							>,
						})),
						uuid: uuid(),
					}),
				),
				permissions,
				userAccounts,
			}
		},
	})
}

export type Group = {
	id: number | null
	name: string | null
	permissions: Array<{
		id: number
		category: string
		displayName: string
		codeName: string
		description: string
		value: Uppercase<ValueOf<typeof PermissionLevel>>
	}>
	userAccounts: Array<{
		id: number
		name: string
		isMember: boolean
	}>
	uuid: string
}

const dataQuery = graphql(`
	query GroupConfigData {
		groups {
			id
			name
			permissions {
				id
				value: level
			}
		}
		permissions {
			id
			category
			codeName
			description
			displayName
		}
		userAccounts {
			id
			name
			groups {
				id
			}
		}
	}
`)
